import { useState, useCallback, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  FileDown,
  ShieldAlert,
  Users,
  Building2,
  CreditCard,
  Archive,
  Scale,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ListChecks,
  RefreshCw,
  Plug,
  KeyRound,
  Shield,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import type { Client, Organization } from '@core/types';
import { orgService } from '@core/supabase/services/orgService';
import { clientService } from '@core/supabase/services/clientService';
import { SplitFlowShell } from '@shared/components/Layout/SplitFlowShell';
import { useToast } from '@shared/hooks/useToast';
import { FlowCoverImage } from '@shared/components/flow/FlowCoverImage';
import { OffboardingFlowCoverPlaceholder } from '@shared/components/flow/OffboardingFlowCoverPlaceholder';
import { offboardingCoverImage } from '@shared/constants/flowIllustrationPaths';
import { ClientOnboardingWizardStepper } from '@modules/clients/ClientOnboardingWizardStepper';
import { downloadOrgTenantFullExportZip } from '@core/export/orgTenantFullExport';

/** SaaS-style tenant exit: scope → export → access → billing → retention → acknowledge. */
const OFFBOARD_STEPS = 6;

const WIZARD_LABELS = ['Scope', 'Export', 'Access', 'Billing', 'Retention', 'Done'] as const;

const STEP_TITLES: Record<number, string> = {
  1: 'Scope',
  2: 'Export tenant data',
  3: 'Access & integrations',
  4: 'Billing & subscription',
  5: 'Retention & legal hold',
  6: 'Wrap-up',
};

/** Left-rail subtitle (same pattern as client onboarding `STEP_SUBTITLES`) */
const STEP_COVER_SUBTITLES: Record<number, string> = {
  1: 'This path is for your consultancy’s GoGrabJob workspace — not for a hiring company you recruit for.',
  2: 'Download the full organisation CSV bundle before continuing. After exit is completed, org-specific row data may be removed; schema and automation stay.',
  3: 'Deactivate users, integrations, and IdP access so the tenant cannot be used.',
  4: 'Clear platform and client balances before continuing — offboarding cannot complete while money is still due.',
  5: 'Confirm export archive, then retention and DPA alignment before wrap-up.',
  6: 'Operational checklist only — no automatic deletion in this UI.',
};

const STEP_NEXT_LABELS: Record<number, string> = {
  1: 'Continue to Export →',
  2: 'Continue to Access →',
  3: 'Continue to Billing →',
  4: 'Continue to Retention →',
  5: 'Continue to wrap-up →',
  6: 'Return to settings →',
};

function formatInr(n: number): string {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Stable left-rail: same title block height every step so illustration slot does not jump */
const OFFBOARDING_COVER_META_CLASS = 'min-h-[13.5rem] sm:min-h-[14rem]';

/** Fixed illustration viewport — same width/height feel on every step */
const OFFBOARDING_ILLUSTRATION_AREA_CLASS =
  'flex-none h-[clamp(320px,46vh,480px)] max-h-[clamp(320px,46vh,480px)] w-full shrink-0 flex-col py-4';


export function OrgOffboardingPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();
  const [step, setStep] = useState(1);
  const [maxReached, setMaxReached] = useState(1);
  const [acknowledged, setAcknowledged] = useState(false);
  /** Step 6 — required acknowledgement before returning to settings */
  const [understandDataDeletion, setUnderstandDataDeletion] = useState(false);
  /** Step 5 — retention & legal checklist */
  const [retentionDpaReviewed, setRetentionDpaReviewed] = useState(false);
  /** ISO timestamp when full ZIP export finished — required to leave step 2 */
  const [exportFullBundleDoneAt, setExportFullBundleDoneAt] = useState<string | null>(null);
  const [exportZipLoading, setExportZipLoading] = useState(false);
  /** Optional self-attestation for area-specific exports from list screens */
  const [optionalExportAck, setOptionalExportAck] = useState({
    applications: false,
    candidates: false,
    clients: false,
    jobsReqs: false,
  });

  /** Step 4 — billing snapshot (org + clients) */
  const [orgBillingSnapshot, setOrgBillingSnapshot] = useState<Organization | null>(null);
  const [clientsBillingSnapshot, setClientsBillingSnapshot] = useState<Client[]>([]);
  const [billingSnapshotLoading, setBillingSnapshotLoading] = useState(false);
  const [billingFetchError, setBillingFetchError] = useState<string | null>(null);
  const [billingFinanceConfirmed, setBillingFinanceConfirmed] = useState(false);

  const fullZipStorageKey = user?.orgId ? `ggj_offboarding_full_zip_${user.orgId}` : null;

  useEffect(() => {
    if (!fullZipStorageKey) return;
    const v = sessionStorage.getItem(fullZipStorageKey);
    if (v) setExportFullBundleDoneAt(v);
  }, [fullZipStorageKey]);

  const wizardNavLabels = useMemo(() => WIZARD_LABELS.map((l) => `[${l}]`), []);

  const bumpReached = useCallback((n: number) => {
    setMaxReached((m) => Math.max(m, n));
  }, []);

  const goToStep = useCallback(
    (n: number) => {
      if (n < 1 || n > OFFBOARD_STEPS || n > maxReached) return;
      setStep(n);
    },
    [maxReached],
  );

  const loadBillingSnapshot = useCallback(async () => {
    if (!user?.orgId) return;
    setBillingSnapshotLoading(true);
    setBillingFetchError(null);
    try {
      const o = await orgService.getOrg(user.orgId);
      if (!o) {
        setOrgBillingSnapshot(null);
        setClientsBillingSnapshot([]);
        setBillingFetchError('Could not load organisation billing.');
        return;
      }
      const list = await clientService.list(user.orgId);
      const clients = Array.isArray(list) ? list : list.data;
      setOrgBillingSnapshot(o as Organization);
      setClientsBillingSnapshot(clients);
    } catch (e) {
      setBillingFetchError(e instanceof Error ? e.message : 'Failed to load balances');
      setOrgBillingSnapshot(null);
      setClientsBillingSnapshot([]);
    } finally {
      setBillingSnapshotLoading(false);
    }
  }, [user?.orgId]);

  useEffect(() => {
    if (step !== 4 || !user?.orgId) return;
    void loadBillingSnapshot();
  }, [step, user?.orgId, loadBillingSnapshot]);

  const billingOutstandingInr = Number(orgBillingSnapshot?.billing_outstanding_inr ?? 0);
  const clientsWithArBalance = useMemo(
    () => clientsBillingSnapshot.filter((c) => Number(c.ar_balance_inr ?? 0) > 0),
    [clientsBillingSnapshot],
  );
  const billingBalancesBlocked =
    billingOutstandingInr > 0 || clientsWithArBalance.length > 0;
  const billingStepReady =
    !billingSnapshotLoading &&
    orgBillingSnapshot != null &&
    !billingFetchError &&
    !billingBalancesBlocked;

  const handleFullOrgExport = useCallback(async () => {
    if (!user?.orgId) return;
    try {
      setExportZipLoading(true);
      await downloadOrgTenantFullExportZip({ orgId: user.orgId, orgName: user.orgName });
      const iso = new Date().toISOString();
      const k = `ggj_offboarding_full_zip_${user.orgId}`;
      sessionStorage.setItem(k, iso);
      setExportFullBundleDoneAt(iso);
    } catch (e) {
      showToast({ title: e instanceof Error ? e.message : 'Export failed', kind: 'error' });
    } finally {
      setExportZipLoading(false);
    }
  }, [user?.orgId, user?.orgName]);

  if (!user?.orgId) return null;

  if (!perms.canManageTeam) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[var(--vscode-bg-primary)] px-4">
        <ShieldAlert className="h-10 w-10 text-amber-500" aria-hidden />
        <p className="max-w-md text-center ggj-text-body vscode-text-secondary">
          Only organisation admins can open this checklist. Ask an owner or admin, or return to settings.
        </p>
        <Link to="/settings/profile" className="btn-primary ggj-text-body">
          Back to settings
        </Link>
      </div>
    );
  }

  const coverTitle = STEP_TITLES[step] ?? STEP_TITLES[1];
  const coverSubtitle = STEP_COVER_SUBTITLES[step] ?? '';

  const illustration = (
    <div className="flex h-full w-full max-h-full max-w-[min(100%,440px)] flex-col items-center justify-center">
      <FlowCoverImage
        src={offboardingCoverImage(step)}
        alt=""
        imgClassName="max-h-full w-full object-contain drop-shadow-xl"
        fallbackSvg={<OffboardingFlowCoverPlaceholder step={step} variant="purple" />}
      />
    </div>
  );

  const STEP_META: Record<number, { icon: React.ReactNode; color: string; bg: string }> = {
    1: { icon: <Archive className="h-5 w-5" />, color: 'var(--ggj-accent)', bg: 'rgba(139,128,249,0.1)' },
    2: { icon: <FileDown className="h-5 w-5" />, color: '#6366f1', bg: 'rgba(99,102,241,0.1)' },
    3: { icon: <Users className="h-5 w-5" />, color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)' },
    4: { icon: <CreditCard className="h-5 w-5" />, color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
    5: { icon: <Scale className="h-5 w-5" />, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
    6: {
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
      ),
      color: 'var(--ggj-accent)',
      bg: 'rgba(139,128,249,0.1)',
    },
  };

  const meta = STEP_META[step] ?? STEP_META[1];
  const stepBadge = `Step ${step} of ${OFFBOARD_STEPS}`;

  type NavProps = { onBack?: () => void; onNext: () => void; nextLabel: string; nextDisabled?: boolean; isLast?: boolean };
  const navProps: NavProps =
    step === 1
      ? {
          onNext: () => {
            bumpReached(2);
            setStep(2);
          },
          nextLabel: STEP_NEXT_LABELS[1],
        }
      : step === 2
        ? {
            onBack: () => goToStep(1),
            onNext: () => {
              bumpReached(3);
              setStep(3);
            },
            nextLabel: STEP_NEXT_LABELS[2],
            nextDisabled: !exportFullBundleDoneAt,
          }
        : step === 3
          ? {
              onBack: () => goToStep(2),
              onNext: () => {
                bumpReached(4);
                setStep(4);
              },
              nextLabel: STEP_NEXT_LABELS[3],
            }
          : step === 4
            ? {
                onBack: () => goToStep(3),
                onNext: () => {
                  bumpReached(5);
                  setStep(5);
                },
                nextLabel: STEP_NEXT_LABELS[4],
                nextDisabled:
                  billingSnapshotLoading ||
                  !orgBillingSnapshot ||
                  Boolean(billingFetchError) ||
                  billingBalancesBlocked ||
                  !billingFinanceConfirmed,
              }
            : step === 5
              ? {
                  onBack: () => goToStep(4),
                  onNext: () => {
                    bumpReached(6);
                    setStep(6);
                  },
                  nextLabel: STEP_NEXT_LABELS[5],
                  nextDisabled: !exportFullBundleDoneAt || !retentionDpaReviewed,
                }
              : {
                  onBack: () => goToStep(5),
                  onNext: () => navigate('/settings/profile'),
                  nextLabel: STEP_NEXT_LABELS[6],
                  nextDisabled: !acknowledged || !understandDataDeletion || !exportFullBundleDoneAt,
                  isLast: true,
                };

  return (
    <>
    {toast}
    <SplitFlowShell
      variant="full"
      rightVariant="theme"
      coverVariant="purple"
      contentMaxClass="max-w-xl lg:max-w-2xl"
      coverTitle={coverTitle}
      coverSubtitle={coverSubtitle}
      stepLabel={wizardNavLabels[step - 1] ?? ''}
      stepCurrent={step}
      stepTotal={OFFBOARD_STEPS}
      illustration={illustration}
      showProgressBar={false}
      showFlowAssetsHint={false}
      coverMetaBlockClassName={OFFBOARDING_COVER_META_CLASS}
      illustrationAreaClassName={OFFBOARDING_ILLUSTRATION_AREA_CLASS}
    >
      <div className="ggj-wizard-form flex h-full min-h-0 w-full flex-1 flex-col">
        {/* ZONE 1 — sticky header */}
        <div className="flex-shrink-0 border-b vscode-border px-6 pb-4 pt-6" style={{ background: 'var(--vscode-bg-secondary)' }}>
          <div className="mb-5 flex items-center justify-between gap-2">
            <Link
              to="/settings/profile"
              className="inline-flex items-center gap-1.5 ggj-text-body font-medium vscode-text-tertiary transition-colors hover:vscode-text-secondary"
            >
              <ArrowLeft className="h-4 w-4" /> Back to organisation settings
            </Link>
            <a
              href="https://gograbjob.com/help"
              target="_blank"
              rel="noreferrer"
              className="ggj-text-meta vscode-text-tertiary underline-offset-2 hover:vscode-text-secondary hover:underline"
            >
              Help
            </a>
          </div>
          <ClientOnboardingWizardStepper
            labels={wizardNavLabels}
            currentStep={step}
            maxReached={maxReached}
            onSelectStep={goToStep}
            navigationAriaLabel="Tenant exit checklist steps"
          />
        </div>

        {/* ZONE 2 — scrollable body */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div className="flex items-center gap-3 pb-1">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]"
              style={{
                background: meta.bg,
                color: meta.color,
                border: `1.5px solid color-mix(in srgb, ${meta.color} 28%, transparent)`,
              }}
            >
              {meta.icon}
            </div>
            <div className="min-w-0">
              <h2 className="text-[16px] font-extrabold leading-tight vscode-text-primary">{coverTitle}</h2>
              <p className="mt-0.5 text-[12px] vscode-text-tertiary">{stepBadge}</p>
            </div>
          </div>
          <div className="vscode-border h-px w-full" />

          {step === 1 && (
            <div className="space-y-5">
              <p className="ggj-text-body leading-relaxed vscode-text-secondary">
                In GoGrabJob Business, words mean two different things — this wizard only covers the first one:
              </p>

              <div className="grid gap-3 sm:grid-cols-1">
                <div className="card border-emerald-500/20 bg-emerald-500/[0.06]">
                  <div className="mb-2 flex items-center gap-2 ggj-text-body font-semibold vscode-text-primary">
                    <Building2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
                    This checklist — your organisation (tenant)
                  </div>
                  <p className="ggj-text-body leading-relaxed vscode-text-secondary">
                    You are winding down or leaving the <strong className="vscode-text-primary">GoGrabJob workspace</strong> for your{' '}
                    <strong className="vscode-text-primary">recruitment consultancy</strong> (the account created in onboarding). Steps follow a common
                    SaaS pattern: <strong className="vscode-text-primary">scope → export data → revoke access → billing → retention</strong>.
                  </p>
                </div>

                <div className="card">
                  <div className="mb-2 flex items-center gap-2 ggj-text-body font-semibold vscode-text-primary">
                    <Users className="h-4 w-4 vscode-text-tertiary" aria-hidden />
                    Not this flow — hiring company “clients”
                  </div>
                  <p className="ggj-text-body leading-relaxed vscode-text-secondary">
                    When your firm <strong className="vscode-text-primary">stops working for a specific employer</strong> you recruit for, that is a{' '}
                    <strong className="vscode-text-primary">Client</strong> record (hiring company) under{' '}
                    <Link to="/clients" className="font-medium text-emerald-600 hover:underline dark:text-emerald-400">
                      Clients
                    </Link>
                    . Close or archive jobs, hand over data per contract, and manage comms —{' '}
                    <strong className="vscode-text-primary">there is no separate “client offboarding” wizard here yet</strong>; use Clients, Jobs, and
                    Applications for that lifecycle.
                  </p>
                </div>
              </div>

              <div className="rounded-[10px] border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700/80 dark:bg-white/[0.03]">
                <p className="ggj-text-meta font-bold uppercase tracking-wide vscode-text-tertiary">What you will walk through</p>
                <ol className="mt-3 list-decimal space-y-2 pl-5 ggj-text-body leading-relaxed vscode-text-secondary">
                  <li>
                    <strong className="vscode-text-primary">Export</strong> — download the full organisation ZIP so you hold a copy of tenant data.
                  </li>
                  <li>
                    <strong className="vscode-text-primary">Access</strong> — turn off users, mail, and integrations so the workspace cannot be misused.
                  </li>
                  <li>
                    <strong className="vscode-text-primary">Billing</strong> — clear recorded balances with finance and support.
                  </li>
                  <li>
                    <strong className="vscode-text-primary">Retention &amp; wrap-up</strong> — confirm legal/retention posture and final acknowledgements.
                  </li>
                </ol>
              </div>

              <p className="ggj-text-meta leading-relaxed vscode-text-tertiary">
                This UI does not cancel your subscription or delete the database by itself. Coordinate approvers, final export dates, and IdP changes
                with your team and support.
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <p className="ggj-text-body leading-relaxed vscode-text-secondary">
                Use the checklist below. <strong className="vscode-text-primary">You must run the full organisation export</strong> (ZIP of CSVs) before
                you can continue — this is the bundle we expect before org-specific data can be cleared from our systems after exit. The{' '}
                <Link to="/clients" className="font-medium text-emerald-600 hover:underline dark:text-emerald-400">
                  Clients
                </Link>{' '}
                area lists <strong className="vscode-text-primary">hiring companies</strong> you recruit for, not your consultancy tenant record.
              </p>

              {/* Required: system export */}
              <div
                className="rounded-[10px] border-2 p-4 sm:p-5"
                style={{
                  borderColor: exportFullBundleDoneAt ? 'rgba(16,185,129,0.45)' : 'rgba(139,128,249,0.45)',
                  background: 'color-mix(in srgb, var(--vscode-bg-elevated) 92%, transparent)',
                }}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-1">
                    <p className="ggj-text-body font-bold vscode-text-primary">Full organisation data export (required)</p>
                    <p className="ggj-text-meta leading-relaxed vscode-text-tertiary">
                      Downloads a ZIP of CSV files: organisation profile, team, clients, candidates, jobs, applications, and requisitions for this
                      tenant. Run this once and store it securely before moving on.
                    </p>
                    {exportFullBundleDoneAt && (
                      <p className="flex items-center gap-1.5 pt-1 ggj-text-meta font-medium text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
                        Bundle downloaded ({new Date(exportFullBundleDoneAt).toLocaleString()})
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleFullOrgExport()}
                    disabled={exportZipLoading}
                    className="flex shrink-0 items-center justify-center gap-2 rounded-[10px] px-4 py-2.5 ggj-text-body font-bold text-white transition-opacity disabled:opacity-60 sm:self-center"
                    style={{
                      background: 'linear-gradient(135deg, #7B6EE8 0%, var(--ggj-accent) 100%)',
                      boxShadow: '0 4px 12px rgba(139,128,249,0.35)',
                    }}
                  >
                    {exportZipLoading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <FileDown className="h-4 w-4" aria-hidden />}
                    {exportZipLoading ? 'Preparing…' : 'Download full export (ZIP)'}
                  </button>
                </div>
              </div>

              {/* Optional: per-area exports */}
              <div>
                <p className="mb-3 ggj-text-meta font-semibold uppercase tracking-wide vscode-text-tertiary">Optional — confirm list exports if you used them</p>
                <div className="divide-y divide-slate-200 rounded-[10px] border border-slate-200 dark:divide-slate-700/80 dark:border-slate-700/80">
                  {(
                    [
                      {
                        key: 'applications' as const,
                        label: 'Applications',
                        hint: 'Pipeline CSV from the list',
                        href: '/applications',
                      },
                      {
                        key: 'candidates' as const,
                        label: 'Candidates',
                        hint: 'Export from the list when available',
                        href: '/candidates',
                      },
                      {
                        key: 'clients' as const,
                        label: 'Clients',
                        hint: 'Hiring-company records (employers)',
                        href: '/clients',
                      },
                      {
                        key: 'jobsReqs' as const,
                        label: 'Jobs · Requisitions',
                        hint: 'Export from each list as needed',
                        href: '/jobs',
                      },
                    ] as const
                  ).map((row) => (
                    <label
                      key={row.key}
                      className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-slate-100/80 dark:hover:bg-white/[0.04]"
                    >
                      <input
                        type="checkbox"
                        className="mt-1 rounded border-[var(--vscode-border-input)] bg-[var(--vscode-bg-input)] text-emerald-600 focus:ring-emerald-500"
                        checked={optionalExportAck[row.key]}
                        onChange={(e) => setOptionalExportAck((prev) => ({ ...prev, [row.key]: e.target.checked }))}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <Link
                            to={row.href}
                            className="ggj-text-body font-semibold text-emerald-600 hover:underline dark:text-emerald-400"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {row.label}
                          </Link>
                        </span>
                        <span className="mt-0.5 block ggj-text-meta vscode-text-tertiary">{row.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="rounded-[10px] border border-[color:color-mix(in_srgb,var(--ggj-accent)_20%,transparent)] bg-[color:color-mix(in_srgb,var(--ggj-accent)_6%,transparent)] px-3 py-3 dark:border-[color:color-mix(in_srgb,var(--ggj-accent)_25%,transparent)] dark:bg-[color:color-mix(in_srgb,var(--ggj-accent)_8%,transparent)]">
                <p className="ggj-text-meta font-semibold vscode-text-primary">Good practice</p>
                <p className="mt-1 ggj-text-meta leading-relaxed vscode-text-secondary">
                  Store the ZIP outside this app (encrypted drive or your records system). If anyone still needs read-only access for handover, use your
                  own process — do not rely on this tenant staying available indefinitely.
                </p>
              </div>

              <p className="ggj-text-meta leading-relaxed vscode-text-tertiary">
                <strong className="vscode-text-secondary">After tenant exit is fully processed:</strong> organisation-specific row data can be deleted from
                our databases while tables, triggers, and other objects remain for the platform. This step ensures you have your own copy first.
              </p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <p className="ggj-text-body leading-relaxed vscode-text-secondary">
                Once data is exported, <strong className="vscode-text-primary">shut down ways back in</strong>: accounts, mail/automation, and any
                corporate identity (SSO) that still trusts this GoGrabJob tenant. Work with your IT and security owners — this screen is a checklist, not
                remote control of your IdP.
              </p>

              <div className="space-y-3">
                <div className="rounded-[10px] border border-slate-200 bg-[color-mix(in_srgb,var(--vscode-bg-elevated)_92%,transparent)] p-4 dark:border-slate-700/80">
                  <div className="flex gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]"
                      style={{
                        background: 'color-mix(in srgb, var(--ggj-accent) 14%, transparent)',
                        color: '#7B6EE8',
                        border: '1.5px solid color-mix(in srgb, var(--ggj-accent) 28%, transparent)',
                      }}
                    >
                      <Users className="h-5 w-5" aria-hidden />
                    </div>
                    <div className="min-w-0 space-y-2">
                      <div>
                        <Link
                          to="/team"
                          className="ggj-text-body font-bold text-[color:var(--ggj-accent)] hover:underline dark:text-[color:var(--ggj-accent)]"
                        >
                          Team
                        </Link>
                        <p className="mt-1 ggj-text-body leading-relaxed vscode-text-secondary">
                          Remove or deactivate people who should not keep signing in. Rotate ownership of billing and admin roles before you delete the
                          last owner-capable account.
                        </p>
                      </div>
                      <ul className="list-inside list-disc space-y-1 ggj-text-meta leading-relaxed vscode-text-tertiary">
                        <li>Deactivate users who have left; revoke invites and pending invitations.</li>
                        <li>Confirm no shared “break-glass” logins still point at this tenant.</li>
                        <li>If you use role-based access, align with HR/offboarding so access matches employment status.</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="rounded-[10px] border border-slate-200 bg-[color-mix(in_srgb,var(--vscode-bg-elevated)_92%,transparent)] p-4 dark:border-slate-700/80">
                  <div className="flex gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]"
                      style={{
                        background: 'color-mix(in srgb, var(--ggj-accent) 14%, transparent)',
                        color: '#7B6EE8',
                        border: '1.5px solid color-mix(in srgb, var(--ggj-accent) 28%, transparent)',
                      }}
                    >
                      <Plug className="h-5 w-5" aria-hidden />
                    </div>
                    <div className="min-w-0 space-y-2">
                      <div>
                        <Link
                          to="/settings/integrations"
                          className="ggj-text-body font-bold text-[color:var(--ggj-accent)] hover:underline dark:text-[color:var(--ggj-accent)]"
                        >
                          Integrations
                        </Link>
                        <p className="mt-1 ggj-text-body leading-relaxed vscode-text-secondary">
                          Disconnect outbound email, webhooks, and API credentials so automations and notifications cannot fire after exit.
                        </p>
                      </div>
                      <ul className="list-inside list-disc space-y-1 ggj-text-meta leading-relaxed vscode-text-tertiary">
                        <li>Remove or rotate SMTP and any API keys stored in settings.</li>
                        <li>Disable calendar or ATS bridges if your team connected them.</li>
                        <li>Check scheduled jobs (reports, reminders) that might still send from this tenant.</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="rounded-[10px] border border-slate-200 bg-[color-mix(in_srgb,var(--vscode-bg-elevated)_92%,transparent)] p-4 dark:border-slate-700/80">
                  <div className="flex gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]"
                      style={{
                        background: 'color-mix(in srgb, var(--ggj-accent) 14%, transparent)',
                        color: '#7B6EE8',
                        border: '1.5px solid color-mix(in srgb, var(--ggj-accent) 28%, transparent)',
                      }}
                    >
                      <KeyRound className="h-5 w-5" aria-hidden />
                    </div>
                    <div className="min-w-0 space-y-2">
                      <p className="ggj-text-body font-bold vscode-text-primary">Identity provider (SSO / IdP)</p>
                      <p className="ggj-text-body leading-relaxed vscode-text-secondary">
                        If users sign in via Keycloak, Entra ID, Okta, or similar, <strong className="vscode-text-primary">disable the application</strong>{' '}
                        or remove the client registration so new sessions cannot be created. Invalidate existing sessions on the IdP side if your policy
                        requires a hard stop.
                      </p>
                      <ul className="list-inside list-disc space-y-1 ggj-text-meta leading-relaxed vscode-text-tertiary">
                        <li>Remove or disable the SAML/OIDC app integration for GoGrabJob Business.</li>
                        <li>Revoke refresh tokens and force re-auth elsewhere if you are decommissioning related systems.</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="rounded-[10px] border border-slate-200 bg-[color-mix(in_srgb,var(--vscode-bg-elevated)_92%,transparent)] p-4 dark:border-slate-700/80">
                  <div className="flex gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]"
                      style={{
                        background: 'color-mix(in srgb, var(--ggj-accent) 14%, transparent)',
                        color: '#7B6EE8',
                        border: '1.5px solid color-mix(in srgb, var(--ggj-accent) 28%, transparent)',
                      }}
                    >
                      <Shield className="h-5 w-5" aria-hidden />
                    </div>
                    <div className="min-w-0 space-y-2">
                      <p className="ggj-text-body font-bold vscode-text-primary">Devices &amp; passwords</p>
                      <p className="ggj-text-body leading-relaxed vscode-text-secondary">
                        Ask users to sign out of browsers and mobile sessions. Rotate any shared passwords or vault entries that referenced this tenant so
                        they cannot be replayed after access should be gone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="ggj-text-meta leading-relaxed vscode-text-tertiary">
                Need help with IdP or a stuck integration? Contact{' '}
                <a href="mailto:business@gograbjob.com" className="font-medium text-[color:var(--ggj-accent)] hover:underline dark:text-[color:var(--ggj-accent)]">
                  business@gograbjob.com
                </a>{' '}
                with your organisation name and a short description of what you have already turned off.
              </p>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <p className="ggj-text-body leading-relaxed vscode-text-secondary">
                Tenant exit cannot be completed while the <strong className="vscode-text-primary">consultancy (organisation)</strong> or a{' '}
                <strong className="vscode-text-primary">hiring company (client)</strong> still has a recorded balance due. Clear balances with finance and
                support, then refresh the figures below.
              </p>

              <div className="rounded-[10px] border border-slate-200 bg-slate-50/90 px-3 py-3 ggj-text-meta leading-relaxed dark:border-slate-700/80 dark:bg-white/[0.03]">
                <p className="font-semibold vscode-text-primary">How to read these figures</p>
                <ul className="mt-2 list-inside list-disc space-y-1.5 vscode-text-secondary">
                  <li>
                    <strong className="vscode-text-primary">Organisation outstanding</strong> is what the platform records as due from your consultancy
                    tenant (subscription or similar).
                  </li>
                  <li>
                    <strong className="vscode-text-primary">Client outstanding</strong> is per hiring company where your commercial terms show an AR
                    balance — each row should read ₹0.00 before you continue.
                  </li>
                  <li>
                    If amounts look wrong after real-world settlement, work with finance and support to correct the source records, then refresh.
                  </li>
                </ul>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => void loadBillingSnapshot()}
                  disabled={billingSnapshotLoading}
                  className="inline-flex items-center gap-2 rounded-[10px] border border-[color-mix(in_srgb,var(--vscode-border-primary)_80%,transparent)] bg-[color-mix(in_srgb,var(--vscode-bg-elevated)_90%,transparent)] px-3 py-2 ggj-text-meta font-semibold vscode-text-primary transition-colors hover:vscode-bg-hover disabled:opacity-60"
                >
                  {billingSnapshotLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                  )}
                  Refresh balances
                </button>
                <Link
                  to="/settings/billing"
                  className="inline-flex items-center gap-1.5 ggj-text-meta font-semibold text-emerald-600 hover:underline dark:text-emerald-400"
                >
                  Billing &amp; plan <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <a
                  href="mailto:business@gograbjob.com"
                  className="ggj-text-meta vscode-text-tertiary hover:vscode-text-secondary"
                >
                  business@gograbjob.com
                </a>
              </div>

              {billingFetchError && (
                <div className="flex items-start gap-2 rounded-[10px] border border-red-500/35 bg-red-500/[0.08] px-3 py-2.5 ggj-text-body text-red-700 dark:text-red-300">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  {billingFetchError}
                </div>
              )}

              <div className="card space-y-4">
                <p className="flex items-center gap-2 ggj-text-body font-semibold vscode-text-primary">
                  <ListChecks className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
                  Billing checklist
                </p>

                <div className="rounded-[10px] border border-slate-200 bg-slate-50/90 p-4 dark:border-slate-700/80 dark:bg-white/[0.03]">
                  <p className="ggj-text-meta font-bold uppercase tracking-wide vscode-text-tertiary">Organisation (tenant)</p>
                  {billingSnapshotLoading && !orgBillingSnapshot ? (
                    <p className="mt-2 ggj-text-body vscode-text-secondary">Loading…</p>
                  ) : orgBillingSnapshot ? (
                    <dl className="mt-3 grid gap-2 ggj-text-body sm:grid-cols-2">
                      <div>
                        <dt className="ggj-text-meta vscode-text-tertiary">Outstanding balance (INR)</dt>
                        <dd
                          className={`mt-0.5 font-semibold tabular-nums ${
                            billingOutstandingInr > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-400'
                          }`}
                        >
                          {formatInr(billingOutstandingInr)}
                          {billingOutstandingInr > 0 ? ' — due' : ' — clear'}
                        </dd>
                      </div>
                      <div>
                        <dt className="ggj-text-meta vscode-text-tertiary">AI credits balance</dt>
                        <dd className="mt-0.5 font-medium tabular-nums vscode-text-primary">
                          {(orgBillingSnapshot.credits_balance ?? 0).toLocaleString('en-IN')}
                        </dd>
                      </div>
                      <div>
                        <dt className="ggj-text-meta vscode-text-tertiary">Plan</dt>
                        <dd className="mt-0.5 vscode-text-primary">{orgBillingSnapshot.plan ?? '—'}</dd>
                      </div>
                      <div>
                        <dt className="ggj-text-meta vscode-text-tertiary">Billing email</dt>
                        <dd className="mt-0.5 truncate vscode-text-primary">{orgBillingSnapshot.billing_email ?? '—'}</dd>
                      </div>
                    </dl>
                  ) : null}
                </div>

                <div className="rounded-[10px] border border-slate-200 bg-slate-50/90 p-4 dark:border-slate-700/80 dark:bg-white/[0.03]">
                  <p className="ggj-text-meta font-bold uppercase tracking-wide vscode-text-tertiary">Hiring companies (clients)</p>
                  <p className="mt-1 ggj-text-meta leading-relaxed vscode-text-tertiary">
                    Optional AR per client (INR). Rows with a balance must be cleared before continuing.
                  </p>
                  {billingSnapshotLoading && clientsBillingSnapshot.length === 0 && !billingFetchError ? (
                    <p className="mt-2 ggj-text-body vscode-text-secondary">Loading…</p>
                  ) : clientsBillingSnapshot.length === 0 ? (
                    <p className="mt-2 ggj-text-body vscode-text-secondary">No clients on record.</p>
                  ) : (
                    <div className="mt-3 overflow-x-auto rounded-[10px] border border-slate-200 dark:border-slate-700/80">
                      <table className="w-full min-w-[280px] text-left ggj-text-body">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-100/80 ggj-text-meta font-semibold uppercase tracking-wide vscode-text-tertiary dark:border-slate-700 dark:bg-white/[0.04]">
                            <th className="px-3 py-2">Client</th>
                            <th className="px-3 py-2 text-right">Outstanding (INR)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...clientsBillingSnapshot]
                            .sort((a, b) => (b.ar_balance_inr ?? 0) - (a.ar_balance_inr ?? 0))
                            .map((c) => {
                              const ar = Number(c.ar_balance_inr ?? 0);
                              return (
                                <tr key={c.id} className="border-b border-slate-100 last:border-0 dark:border-slate-800/80">
                                  <td className="px-3 py-2">
                                    <Link
                                      to={`/clients/${c.id}`}
                                      className="font-medium text-emerald-600 hover:underline dark:text-emerald-400"
                                    >
                                      {c.name || '—'}
                                    </Link>
                                  </td>
                                  <td
                                    className={`px-3 py-2 text-right font-medium tabular-nums ${
                                      ar > 0 ? 'text-amber-700 dark:text-amber-300' : 'vscode-text-secondary'
                                    }`}
                                  >
                                    {formatInr(ar)}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {billingBalancesBlocked && orgBillingSnapshot && !billingSnapshotLoading && (
                  <div className="flex items-start gap-2 rounded-[10px] border border-amber-500/40 bg-amber-500/[0.09] px-3 py-2.5 ggj-text-body text-amber-950 dark:text-amber-200">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>
                      <strong className="font-semibold">Balances are still due.</strong> Settle or adjust amounts in your finance process (and in the
                      database via support) so outstanding values read <strong>₹0.00</strong>, then refresh. You cannot continue to Retention until
                      cleared.
                    </span>
                  </div>
                )}

                <label className="flex cursor-pointer items-start gap-3 ggj-text-body vscode-text-secondary">
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-[var(--vscode-border-input)] bg-[var(--vscode-bg-input)] text-emerald-600 focus:ring-emerald-500"
                    checked={billingFinanceConfirmed}
                    onChange={(e) => setBillingFinanceConfirmed(e.target.checked)}
                    disabled={!billingStepReady}
                  />
                  <span>
                    I confirm with finance that subscription, invoices, and payment methods are aligned;{' '}
                    <strong className="vscode-text-primary">no further platform charges</strong> are pending for this tenant beyond what is shown above
                    (all zeros for outstanding).
                  </span>
                </label>
              </div>

              <p className="ggj-text-meta leading-relaxed vscode-text-tertiary">
                This screen does not charge cards or cancel plans automatically. Balances are read from organisation and client records — update them
                through your commercial process and support so the database reflects reality.
              </p>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <p className="ggj-text-body leading-relaxed vscode-text-secondary">
                Work through the checklist below. The <strong className="vscode-text-primary">full organisation CSV bundle (ZIP)</strong> must already be
                on record from the Export step — retention and legal sign-off build on that archive.
              </p>

              <div className="rounded-[10px] border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700/80 dark:bg-white/[0.03]">
                <p className="ggj-text-meta font-bold uppercase tracking-wide vscode-text-tertiary">Often reviewed alongside retention</p>
                <ul className="mt-3 list-inside list-disc space-y-2 ggj-text-body leading-relaxed vscode-text-secondary">
                  <li>
                    <strong className="vscode-text-primary">Legal hold</strong> — whether any matter still requires keeping certain records beyond normal
                    retention.
                  </li>
                  <li>
                    <strong className="vscode-text-primary">Candidate &amp; client comms</strong> — who owns final notifications and data return under
                    contract.
                  </li>
                  <li>
                    <strong className="vscode-text-primary">Subprocessors &amp; DPA</strong> — that your documented posture matches what you told
                    customers and regulators.
                  </li>
                  <li>
                    <strong className="vscode-text-primary">Backups &amp; exports</strong> — that the ZIP and any other copies sit in the right secure
                    location for your policy.
                  </li>
                </ul>
              </div>

              <div className="card space-y-4">
                <p className="flex items-center gap-2 ggj-text-body font-semibold vscode-text-primary">
                  <ListChecks className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
                  Retention &amp; legal checklist
                </p>

                <div
                  className={`rounded-[10px] border px-3 py-3 sm:px-4 ${
                    exportFullBundleDoneAt
                      ? 'border-emerald-500/35 bg-emerald-500/[0.06]'
                      : 'border-amber-500/40 bg-amber-500/[0.08]'
                  }`}
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="ggj-text-body font-medium vscode-text-primary">Full organisation data export (CSV bundle / ZIP)</p>
                      <p className="mt-1 ggj-text-meta leading-relaxed vscode-text-tertiary">
                        System-generated ZIP of tenant tables — required before you can finish retention or wrap-up.
                      </p>
                      {exportFullBundleDoneAt ? (
                        <p className="mt-2 flex items-center gap-1.5 ggj-text-meta font-medium text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
                          Recorded {new Date(exportFullBundleDoneAt).toLocaleString()}
                        </p>
                      ) : (
                        <p className="mt-2 flex items-center gap-1.5 ggj-text-meta font-medium text-amber-800 dark:text-amber-300">
                          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                          Not recorded — complete the Export step first.
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                      {!exportFullBundleDoneAt && (
                        <button
                          type="button"
                          onClick={() => goToStep(2)}
                          className="ggj-text-body font-semibold text-emerald-600 underline-offset-2 hover:underline dark:text-emerald-400"
                        >
                          Go to Export
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => void handleFullOrgExport()}
                        disabled={exportZipLoading}
                        className="flex items-center justify-center gap-2 rounded-[10px] px-3 py-2 ggj-text-meta font-bold text-white transition-opacity disabled:opacity-60"
                        style={{
                          background: 'linear-gradient(135deg, #7B6EE8 0%, var(--ggj-accent) 100%)',
                          boxShadow: '0 2px 10px rgba(139,128,249,0.35)',
                        }}
                      >
                        {exportZipLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <FileDown className="h-3.5 w-3.5" aria-hidden />}
                        {exportZipLoading
                          ? 'Preparing…'
                          : exportFullBundleDoneAt
                            ? 'Download again (ZIP)'
                            : 'Download full export (ZIP)'}
                      </button>
                    </div>
                  </div>
                </div>

                <label className="flex cursor-pointer items-start gap-3 ggj-text-body vscode-text-secondary">
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-[var(--vscode-border-input)] bg-[var(--vscode-bg-input)] text-emerald-600 focus:ring-emerald-500"
                    checked={retentionDpaReviewed}
                    onChange={(e) => setRetentionDpaReviewed(e.target.checked)}
                  />
                  <span>
                    I confirm that <strong className="vscode-text-primary">retention (days)</strong>, <strong className="vscode-text-primary">DPA</strong>
                    , and <strong className="vscode-text-primary">consent</strong> settings for this tenant have been reviewed with the right owner and
                    align with your policy and local law (what you keep vs delete). Automated purge across all tables remains a product follow-up.
                  </span>
                </label>
              </div>

              <p className="ggj-text-meta leading-relaxed vscode-text-tertiary">
                Update org fields in <strong className="vscode-text-secondary">Settings → Organisation / profile</strong> if you still need to change
                values on record — this wizard only captures your attestation.
              </p>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-5">
              <p className="ggj-text-body leading-relaxed vscode-text-secondary">
                You are at the final step. Confirm you have a secure copy of your data (including the full organisation ZIP from the Export step),
                that owners are aligned, and that you understand what happens when this exit is processed in the backend.
              </p>

              <p className="ggj-text-body leading-relaxed vscode-text-secondary">
                Tell your internal stakeholders (finance, IT, legal, and account owners) that{' '}
                <strong className="vscode-text-primary">exit timing</strong> is coordinated outside this screen — support or automation applies the final
                state after your organisation confirms. Keep an offline note of who approved export and access changes if your audit policy requires it.
              </p>

              <div className="rounded-[10px] border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700/80 dark:bg-white/[0.03]">
                <p className="mb-3 flex items-center gap-2 ggj-text-meta font-bold uppercase tracking-wide vscode-text-tertiary">
                  <ListChecks className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
                  Exit journey summary
                </p>
                <ul className="space-y-2 ggj-text-body vscode-text-secondary">
                  <li>
                    <span className="font-medium vscode-text-primary">Scope</span> — tenant vs hiring-company clients clarified.
                  </li>
                  <li>
                    <span className="font-medium vscode-text-primary">Export</span> — full org CSV bundle (ZIP) plus any list exports you needed.
                  </li>
                  <li>
                    <span className="font-medium vscode-text-primary">Access</span> — team, integrations, and IdP access addressed.
                  </li>
                  <li>
                    <span className="font-medium vscode-text-primary">Billing</span> — subscription and payment alignment with finance.
                  </li>
                  <li>
                    <span className="font-medium vscode-text-primary">Retention</span> — policy and compliance fields reviewed.
                  </li>
                </ul>
              </div>

              <div className="card space-y-4">
                <p className="ggj-text-body font-semibold vscode-text-primary">Final checklist</p>

                <div
                  className={`rounded-[10px] border px-3 py-3 sm:px-4 ${
                    exportFullBundleDoneAt
                      ? 'border-emerald-500/35 bg-emerald-500/[0.06]'
                      : 'border-amber-500/40 bg-amber-500/[0.08]'
                  }`}
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="ggj-text-body font-medium vscode-text-primary">Full organisation data export (ZIP)</p>
                      <p className="mt-1 ggj-text-meta leading-relaxed vscode-text-tertiary">
                        Required before finishing this tenant exit. The bundle includes CSV extracts for your organisation’s data.
                      </p>
                      {exportFullBundleDoneAt ? (
                        <p className="mt-2 flex items-center gap-1.5 ggj-text-meta font-medium text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
                          Completed {new Date(exportFullBundleDoneAt).toLocaleString()}
                        </p>
                      ) : (
                        <p className="mt-2 flex items-center gap-1.5 ggj-text-meta font-medium text-amber-800 dark:text-amber-300">
                          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                          Not recorded in this session — go back to Export and download the ZIP.
                        </p>
                      )}
                    </div>
                    {!exportFullBundleDoneAt && (
                      <button
                        type="button"
                        onClick={() => goToStep(2)}
                        className="shrink-0 ggj-text-body font-semibold text-emerald-600 underline-offset-2 hover:underline dark:text-emerald-400"
                      >
                        Go to Export step
                      </button>
                    )}
                  </div>
                </div>

                <label className="flex cursor-pointer items-start gap-3 ggj-text-body vscode-text-secondary">
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-[var(--vscode-border-input)] bg-[var(--vscode-bg-input)] text-emerald-600 focus:ring-emerald-500"
                    checked={understandDataDeletion}
                    onChange={(e) => setUnderstandDataDeletion(e.target.checked)}
                  />
                  <span>
                    I understand that after this exit is processed, <strong className="vscode-text-primary">organisation-specific row data</strong> may
                    be removed from hosted databases for this tenant. <strong className="vscode-text-primary">Table structures, triggers, and other
                    platform objects</strong> remain — only your tenant’s business rows are targeted for deletion.
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-3 ggj-text-body vscode-text-secondary">
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-[var(--vscode-border-input)] bg-[var(--vscode-bg-input)] text-emerald-600 focus:ring-emerald-500"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                  />
                  <span>
                    I have completed or assigned the tenant-exit steps to the right owners, and will keep an audit trail (who exported what, when access
                    was revoked) outside this app if required.
                  </span>
                </label>
              </div>

              <p className="ggj-text-meta leading-relaxed vscode-text-tertiary">
                This screen does not delete data by itself — it records your confirmation. Backend deletion runs only after your organisation’s exit is
                processed through support or automation.
              </p>
            </div>
          )}
        </div>

        {/* ZONE 3 — sticky footer */}
        <div className="flex-shrink-0 border-t vscode-border px-6 py-4" style={{ background: 'var(--vscode-bg-secondary)' }}>
          <div className="flex items-center gap-3">
            {navProps.onBack ? (
              <button
                type="button"
                onClick={navProps.onBack}
                className="flex items-center gap-2 rounded-[10px] px-5 py-3 ggj-text-body font-bold transition-all"
                style={{
                  color: 'var(--ggj-accent)',
                  background: 'rgba(139,128,249,0.08)',
                  border: '1.5px solid rgba(139,128,249,0.28)',
                }}
              >
                <ArrowLeft className="h-4 w-4 shrink-0" />
                Back
              </button>
            ) : (
              <div className="w-[84px] shrink-0" />
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={navProps.onNext}
              disabled={navProps.nextDisabled}
              title={
                step === 2 && !exportFullBundleDoneAt
                  ? 'Download the full organisation export (ZIP) first'
                  : step === 4 &&
                      (billingSnapshotLoading ||
                        !orgBillingSnapshot ||
                        Boolean(billingFetchError) ||
                        billingBalancesBlocked ||
                        !billingFinanceConfirmed)
                    ? 'Load balances, clear all outstanding amounts to ₹0, then confirm with finance'
                    : step === 5 && (!exportFullBundleDoneAt || !retentionDpaReviewed)
                      ? 'Record full export ZIP and confirm retention / DPA review'
                      : step === 6 && (!acknowledged || !understandDataDeletion || !exportFullBundleDoneAt)
                        ? 'Confirm export, data-deletion understanding, and owner assignment'
                        : undefined
              }
              className="flex min-w-[190px] items-center justify-center gap-2 rounded-[10px] px-8 py-3 ggj-text-body font-bold text-white transition-all disabled:opacity-60"
              style={{
                background: navProps.isLast ? 'linear-gradient(135deg, #7B6EE8 0%, var(--ggj-accent) 100%)' : 'var(--ggj-accent)',
                boxShadow: '0 4px 16px rgba(139,128,249,0.38)',
              }}
            >
              <span>{navProps.nextLabel}</span>
            </button>
          </div>
        </div>
      </div>
    </SplitFlowShell>
  </>
  );
}